import { useState, useEffect } from 'react';
import { jiraTokenExpiryService, type JiraAuthError } from '@/services/jira-token-expiry';
import * as api from '@/services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faGear,
  faRotate,
  faTriangleExclamation,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

interface JiraTokenExpiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  error?: JiraAuthError;
  onSettingsNavigate?: () => void;
}

export function JiraTokenExpiryModal({ 
  isOpen, 
  onClose, 
  error, 
  onSettingsNavigate 
}: JiraTokenExpiryModalProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState<{ success: boolean; message: string } | null>(null);

  // Clear retry result when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setRetryResult(null);
    }
  }, [isOpen]);

  const handleRetry = async () => {
    setIsRetrying(true);
    setRetryResult(null);

    try {
      const settings = await api.getJiraSettings();
      if (!settings) {
        setRetryResult({ 
          success: false, 
          message: 'No Jira settings found. Please configure your Jira integration.' 
        });
        return;
      }

      const result = await jiraTokenExpiryService.forceValidation(settings);
      
      if (result.valid) {
        setRetryResult({ 
          success: true, 
          message: 'Jira connection is now working!' 
        });
        // Auto-close after success
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setRetryResult({ 
          success: false, 
          message: result.error?.message || 'Still unable to connect to Jira.' 
        });
      }
    } catch (err: any) {
      setRetryResult({ 
        success: false, 
        message: err.message || 'Failed to retry connection.' 
      });
    } finally {
      setIsRetrying(false);
    }
  };

  const getErrorDescription = (type: JiraAuthError['type']) => {
    switch (type) {
      case 'expired':
        return 'Your Jira API token has expired or is no longer valid.';
      case 'invalid':
        return 'Your Jira credentials are incorrect or incomplete.';
      case 'network':
        return 'Unable to connect to Jira. Please check your network connection.';
      default:
        return 'There\'s an issue with your Jira authentication.';
    }
  };

  const getErrorActions = (type: JiraAuthError['type']) => {
    switch (type) {
      case 'expired':
        return [
          'Generate a new API token in your Atlassian account',
          'Update the token in your Jira settings',
          'Ensure the token has the required permissions'
        ];
      case 'invalid':
        return [
          'Check that your domain is correct (e.g., "company" for company.atlassian.net)',
          'Verify your email address is correct',
          'Ensure your API token is valid and not truncated'
        ];
      case 'network':
        return [
          'Check your internet connection',
          'Verify you can access your Jira instance in a browser',
          'Check if your company has firewall restrictions'
        ];
      default:
        return [
          'Try refreshing the page',
          'Check your Jira settings',
          'Contact support if the issue persists'
        ];
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-screen px-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <FontAwesomeIcon icon={faTriangleExclamation} className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Jira Connection Issue
                </h2>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {error?.type === 'expired' ? 'Token Expired' : 'Authentication Failed'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-gray-500 dark:text-slate-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Error Description */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg">
              <FontAwesomeIcon icon={faTriangleExclamation} className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  {error ? getErrorDescription(error.type) : 'Unable to connect to Jira.'}
                </p>
                {error?.originalError && (
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1 font-mono">
                    {error.originalError}
                  </p>
                )}
              </div>
            </div>

            {/* Recommended Actions */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Recommended Actions:
              </h3>
              <ul className="space-y-2">
                {getErrorActions(error?.type || 'unknown').map((action, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    <span className="text-sm text-gray-600 dark:text-slate-300">{action}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Retry Result */}
            {retryResult && (
              <div className={`p-3 rounded-lg border ${
                retryResult.success 
                  ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20' 
                  : 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20'
              }`}>
                <p className={`text-sm ${
                  retryResult.success 
                    ? 'text-emerald-800 dark:text-emerald-200' 
                    : 'text-red-800 dark:text-red-200'
                }`}>
                  {retryResult.message}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-slate-700">
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
            >
              {isRetrying ? (
                <>
                  <FontAwesomeIcon icon={faRotate} className="h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faRotate} className="h-4 w-4" />
                  Test Connection
                </>
              )}
            </button>
            
            {onSettingsNavigate && (
              <button
                onClick={() => {
                  onClose();
                  onSettingsNavigate();
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200 text-sm font-medium rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faGear} className="h-4 w-4" />
                Settings
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6">
            <a
              href="https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 transition-colors"
            >
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
              Learn how to manage Atlassian API tokens
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hook for components to use the token expiry modal
export function useJiraTokenExpiryModal(onSettingsNavigate?: () => void) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentError, setCurrentError] = useState<JiraAuthError | undefined>();

  useEffect(() => {
    const unsubscribe = jiraTokenExpiryService.onTokenExpired((error) => {
      setCurrentError(error);
      setIsOpen(true);
    });

    return unsubscribe;
  }, []);

  const showModal = (error?: JiraAuthError) => {
    setCurrentError(error);
    setIsOpen(true);
  };

  const hideModal = () => {
    setIsOpen(false);
  };

  const Modal = () => (
    <JiraTokenExpiryModal
      isOpen={isOpen}
      onClose={hideModal}
      error={currentError}
      onSettingsNavigate={onSettingsNavigate}
    />
  );

  return {
    showModal,
    hideModal,
    Modal
  };
}
